import { registerPlugin, Capacitor } from '@capacitor/core';
import axios from 'axios';

/**
 * Plugin nativo (android/.../TrackerPlugin.kt) que corre o Traccar Client SDK:
 * recolhe posições em segundo plano, guarda-as numa fila local e envia-as por
 * POST para o serverUrl. Só existe dentro da app Android; no browser não há nada.
 */
export const Tracker = registerPlugin('Tracker');

export const isNativeApp = () => Capacitor.isNativePlatform();

const CHAVE_INICIO = 'trackerShiftStartedAt';
// Rede de segurança para quando o técnico não faz logout (esquecimento, app
// fechada à força, bateria morta): sem isto, o tracker ficava ligado
// indefinidamente. Não é à prova de tudo — um reinício do tablet retoma o
// tracking sozinho via o BootReceiver do próprio SDK, sem passar por aqui —
// mas cobre o caso mais comum (app aberta/em fundo, turno esquecido).
const DURACAO_MAX_MS = 14 * 60 * 60 * 1000; // 14h: cobre o pior turno plausível + folga

const CONFIG_PADRAO = {
  accuracy: 'HIGH',
  distanceMeters: 25,
  intervalSeconds: 30,
  heartbeatIntervalSeconds: 120,
  stopDetection: true,
  wakeLock: false,
  buffer: true,
};

let timeoutSeguranca = null;

function agendarParagemSeguranca() {
  clearTimeout(timeoutSeguranca);
  const inicio = Number(localStorage.getItem(CHAVE_INICIO) || Date.now());
  const restante = DURACAO_MAX_MS - (Date.now() - inicio);
  timeoutSeguranca = setTimeout(() => { pararTurno(); }, Math.max(0, restante));
}

/**
 * Chamar quando o técnico entra na área /tecnico autenticado (login novo, ou
 * app reaberta com sessão ainda válida). Idempotente: se já estiver a
 * correr, só atualiza a configuração.
 */
export async function iniciarTurno() {
  if (!isNativeApp()) return false;
  const utilizador = JSON.parse(localStorage.getItem('user') || 'null');
  if (!utilizador?.id) return false;

  // Se a app reabriu depois de já ter passado da duração máxima (reiniciou o
  // tablet, ficou fechada dias), força a paragem do turno "antigo" primeiro.
  const inicioAnterior = Number(localStorage.getItem(CHAVE_INICIO) || 0);
  if (inicioAnterior && Date.now() - inicioAnterior > DURACAO_MAX_MS) {
    await pararTurno();
  }

  // Trinca de segurança: nunca chamar Tracker.start() sem confirmar primeiro
  // que o Android tem mesmo a localização "sempre" concedida. Sem isto, o
  // SDK tenta arrancar o seu serviço em primeiro plano (TrackerService) na
  // mesma — e nalgumas condições (ex.: o serviço já tinha falhado antes e o
  // Android está a tentar repô-lo sozinho) isso pode levar a um crash da app
  // em vez de uma simples rejeição. É mais barato confirmar aqui do que
  // arriscar chegar lá.
  const estado = await verificarLocalizacao();
  if (!estado.fine || !estado.background || !estado.gps) {
    throw new Error('Localização "permitir sempre" não concedida — ecrã de bloqueio devia ter impedido isto.');
  }

  // O env pode estar vazio (o nginx trata do /api). O SDK nativo precisa de um
  // URL absoluto, por isso usamos a origem da página, que na app Android é o
  // server.url do Capacitor.
  const baseURL = process.env.VUE_APP_URL_BASE || window.location.origin;
  const { data } = await axios.get(`${baseURL}/api/v1/tracker/credencial/`);
  const deviceId = data.data.deviceId;

  await Tracker.start({
    ...CONFIG_PADRAO,
    serverUrl: `${baseURL}/api/v1/tracker/registrar/`,
    deviceId,
  });

  localStorage.setItem(CHAVE_INICIO, String(Date.now()));
  agendarParagemSeguranca();
  return true;
}

/** Chamar no logout, ou pela rede de segurança acima. */
export async function pararTurno() {
  if (!isNativeApp()) return;
  clearTimeout(timeoutSeguranca);
  timeoutSeguranca = null;
  localStorage.removeItem(CHAVE_INICIO);
  try {
    await Tracker.stop();
  } catch (e) {
    // Já parado ou nunca chegou a arrancar — sem problema.
  }
}

/**
 * Estado da localização no aparelho: { fine, gps, background }.
 * Fora da app Android não há nada a exigir, por isso devolve tudo a true.
 */
export async function verificarLocalizacao() {
  if (!isNativeApp()) return { fine: true, gps: true, background: true };
  return Tracker.checkLocation();
}

/** Mostra os pedidos de permissão do sistema (precisa e, depois, "sempre"). */
export async function pedirLocalizacao() {
  return Tracker.requestLocation();
}

export const abrirDefinicoesLocalizacao = () => Tracker.openLocationSettings();
export const abrirDefinicoesApp = () => Tracker.openAppSettings();
